import { useQuery } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { IProductB2C, IProductDetail, IProductLineDetail } from '../../../model/product.interface'
import { IPriceInfo } from '../../../model/purchase.interface'
import { getJwtToken } from '../../auth'
import useTranslation from '../../i18n/useTranslation'
import {
  customerTypeQueryApi,
  productDetailQueryApi,
  productsB2BQueryApi,
  productsB2CQueryApi,
  productsLineCompanyQueryApi,
  productsLineDetailQueryApi,
} from '../../react-query/queryApis'

// export enum Title {
//   매주수업듣기할인 = '매주 수업 듣기 할인',
//   패키지할인 = '패키지 할인',
// }

// export interface Properties {
//   is_popular: boolean
//   hold_activation: boolean
// }

export const useProductB2C = (currency: string = undefined, discount: boolean, applyDiscountCoupon: boolean) => {
  const { locale } = useTranslation()

  const searchParams = {
    locale,
    currency: currency,
    discount,
    apply_discount_coupon: applyDiscountCoupon,
  }

  return useQuery<IProductB2C, AxiosError>({
    queryKey: productsB2CQueryApi.getQueryKey({ searchParams }),
    queryFn: productsB2CQueryApi.getQueryFn({ searchParams }),
    // onError: (e) => handleAxiosError(e),
    retry: false,
  })
}

export const useProductB2B = (currency: string = undefined, discount: boolean) => {
  const { locale } = useTranslation()

  const searchParams = {
    locale,
    currency: currency,
    discount,
  }

  return useQuery<IProductB2C, AxiosError>({
    queryKey: productsB2BQueryApi.getQueryKey({ searchParams }),
    queryFn: productsB2BQueryApi.getQueryFn({ searchParams }),

    // onError: (e) => handleAxiosError(e),
    retry: false,
  })
}

export const CustomerType = {
  B2C: 'b2c',
  B2C_AFFILIATION: 'b2c_affiliation',

  B2B2C: 'b2b2c',

  B2B2C_HR: 'b2b2c_hr',
  B2B2C_HR_ROUND: 'b2b2c_hr_round',
  B2B2C_HR_ROUND_SPLIT: 'b2b2c_hr_round_split',
  B2B_HR: 'b2b_hr',

  MULTICAMPUS: 'multicampus',
  EXPORT_VOUCHER: 'export_voucher',
  K_VOUCHER: 'k_voucher',

  B2B2C_HR_SPLIT: 'b2b2c_hr_split',
  B2B_BULK: 'b2b_bulk',

  B2B2C_HR_DEPOSIT: 'b2b2c_hr_deposit',
  B2B2C_HR_ROUND_DEPOSIT: 'b2b2c_hr_round_deposit',
}

type CustomerType = keyof typeof CustomerType
type CustomerTypeResponse = {
  customer_type: CustomerType
  is_b2c_eligible: boolean
  success: boolean
}

export interface SelectedCustomerType extends CustomerTypeResponse {
  customer_type: CustomerType
  success: boolean
  isB2CEligible: boolean
  isMulticampus: boolean
  isDirectPurchaser: boolean // 직접 구매가 가능한지 여부
  requiresAttendanceVerification: boolean // SectionB2BProgress, 출석률 인증이 필요한지 여부
}
export const useCustomerType = (config = {}) => {
  return useQuery<CustomerTypeResponse, AxiosError, SelectedCustomerType>({
    queryKey: customerTypeQueryApi.getQueryKey(),
    queryFn: customerTypeQueryApi.getQueryFn(),
    staleTime: 5 * 60 * 1000, // 5 minutes (300,000 ms)
    cacheTime: 5 * 60 * 1000,
    ...config,
    select(data): SelectedCustomerType {
      const isMulticampus = data.customer_type === CustomerType.MULTICAMPUS
      const isDirectPurchaser =
        data.customer_type !== CustomerType.B2B_HR &&
        data.customer_type !== CustomerType.EXPORT_VOUCHER &&
        !isMulticampus
      const requiresAttendanceVerification =
        !data.is_b2c_eligible &&
        data.customer_type !== CustomerType.EXPORT_VOUCHER &&
        data.customer_type !== CustomerType.K_VOUCHER

      return {
        ...data,
        isB2CEligible: data.is_b2c_eligible,
        isMulticampus,
        isDirectPurchaser,
        requiresAttendanceVerification,
      }
    },
  })
}

export const useProductLineDetail = (currency: string = undefined, productLineId) => {
  const pathParams = {
    productLineId: productLineId,
  }
  const searchParams = {
    currency: currency,
  }

  return useQuery<IProductLineDetail, AxiosError>({
    queryKey: [productsLineDetailQueryApi.getQueryKey({ pathParams, searchParams })],
    queryFn: productsLineDetailQueryApi.getQueryFn({ pathParams, searchParams }),

    // onError: (e) => handleAxiosError(e),
    retry: false,
    refetchOnWindowFocus: true,
  })
}

export interface IProductLineCompany {
  type: string
  ko: En
  en: En
  ja: En
  zh_cht: En
  zh_chs: En
}

export interface En {
  desktop: Desktop[]
  mobile: Desktop[]
}

export interface Desktop {
  img_url: string
  background_color: string
  button: Button
}

export interface Button {
  enabled: boolean
  url: string
  bg_color: string
  text_color: string
  text: string
}

export const useProductLineCompany = (currency: string = undefined) => {
  const searchParams = {
    currency: currency,
  }

  return useQuery<IProductLineCompany, AxiosError>({
    queryKey: [productsLineCompanyQueryApi.getQueryKey({ searchParams })],
    queryFn: productsLineCompanyQueryApi.getQueryFn({ searchParams }),

    // onError: (e) => handleAxiosError(e),
    retry: false,
    refetchOnWindowFocus: true,
    enabled: !!getJwtToken(),
  })
}

export const useProductDetail = (productId: number, country_code = 'KR') => {
  // dependant queries
  const { locale } = useTranslation()
  const pathParams = { productId }
  const searchParams = { country_code: country_code }

  return useQuery<IProductDetail, AxiosError, IPriceInfo>({
    queryKey: productDetailQueryApi.getQueryKey({ pathParams, searchParams }),
    queryFn: productDetailQueryApi.getQueryFn({ pathParams, searchParams }),

    // onError: (e) => handleAxiosError(e),
    enabled: !!productId,
    cacheTime: 1000 * 60 * 24,
    staleTime: 1000 * 60 * 24,
  })
}
